import { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { authService } from '@/services/auth.service';
import { Loader2 } from 'lucide-react';

interface BackendUser {
  userId: number;
  email: string;
  name: string;
}

interface AuthContextType {
  user: BackendUser | null;
  loading: boolean;
  setUser: (user: BackendUser | null | ((prev: BackendUser | null) => BackendUser | null)) => void;
  signInWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  isLoggingOut: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<BackendUser | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Add isLoggingOut state
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  useEffect(() => {
    // Set up Supabase auth listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_OUT') {
          setUser(null);
          localStorage.removeItem('token');
        }
      }
    );

    // Check session immediately on mount
    checkSession();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const checkSession = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setUser(null);
        setLoading(false);
        return;
      }

      const userData = await authService.verifyToken(token);
      setUser(userData);
    } catch (error) {
      console.error('Session check error:', error);
      localStorage.removeItem('token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    try {
      setLoading(true);
      const result = await authService.signInWithGoogle();
      
      // Handle the redirect - the actual profile will be set in the callback
      if (result.url) {
        window.location.href = result.url;
      }
    } catch (error: any) {
      console.error('Google sign in error:', error);
      setUser(null);
      toast({
        title: 'Authentication Error',
        description: error.message || 'Failed to sign in with Google. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Wrap logout in context
  const logout = async () => {
    setIsLoggingOut(true);
    try {
      // Pass the user ID to authService.logout if user exists
      await authService.logout(user?.userId?.toString());
      setUser(null);
      navigate('/auth', { replace: true });
    } catch (error) {
      console.error('Context logout error:', error);
      toast({
        title: 'Error',
        description: 'Failed to logout. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoggingOut(false);
    }
  };

  // Update ProtectedRoute to handle loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, loading, setUser, signInWithGoogle, logout, isLoggingOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}